import { useCallback, useEffect, useRef, useState } from "react";
import { listEventsWithoutCampaign, listSurveySources } from "../api";
import { SelectSearchOption } from "../../../components/searchable-select";

type OpportunitySourceType = "Event" | "Survey";

interface UseOpportunitySourcesResult {
  data: SelectSearchOption[];
  isLoading: boolean;
  reload: () => Promise<void>;
  search: (value: string) => void;
}

export function useOpportunitySources(
  type: OpportunitySourceType,
  opportunityId?: string,
): UseOpportunitySourcesResult {
  const [data, setData] = useState<SelectSearchOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const requestIdRef = useRef(0);

  const loadSources = useCallback(async (search = searchTerm) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    try {
      if (type === "Event") {
        const response = await listEventsWithoutCampaign({
          search: search.trim() || undefined,
          page: 0,
          size: 50,
          opportunityId,
        });

        if (requestId !== requestIdRef.current) return;

        setData(
          response.list.map((item) => ({
            value: item.id,
            label: item.name,
            description: item.description || "No description",
            keywords: [item.name, item.description || ""],
          })),
        );
        return;
      }

      const response = await listSurveySources({
        search: search.trim() || undefined,
        page: 0,
        size: 50,
        opportunityId,
      });

      if (requestId !== requestIdRef.current) return;

      setData(
        response.list.map((item) => ({
          value: item.id,
          label: item.name,
          description: item.description || "No description",
          keywords: [item.name, item.description || ""],
        })),
      );
    } catch (error) {
      if (requestId === requestIdRef.current) setData([]);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [opportunityId, searchTerm, type]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadSources(searchTerm);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [loadSources, searchTerm]);

  return {
    data,
    isLoading,
    reload: () => loadSources(),
    search: setSearchTerm,
  };
}
